// @ts-ignore
// @ts-nocheck

import axios from 'axios';
import store from '../store';
import authSlice from '../store/slices/auth';
import { VITE_API_URL } from './constants';

const baseURL = VITE_API_URL;
const axiosService = axios.create({ baseURL });

const clearUser = () => {
    store.dispatch(authSlice.actions.setLogout());
};

function redirectIfDisabled(error) {
    const data = error?.response?.data;
    if (error?.response?.status !== 403 || data?.code !== 'ACCOUNT_DISABLED') return false;
    if (data.jailToken) sessionStorage.setItem('jw.jailToken', data.jailToken);
    clearUser();
    if (!window.location.pathname.startsWith('/jail')) window.location.replace('/jail');
    return true;
}

axiosService.interceptors.request.use(async (config) => {
    const { token, accessExpiry } = store.getState().auth;

    // If there's no token, don't try to refresh
    if (!token) {
        return config;
    }

    // Set the token in headers
    config.headers.Authorization = `Bearer ${token}`;

    // Check if the access token is expired or about to expire (e.g., within a minute)
    if (accessExpiry && new Date(accessExpiry).getTime() < Date.now() + 60000) {
        try {
            const newAccessToken = await refreshAccessToken();
            if (newAccessToken) {
                config.headers.Authorization = `Bearer ${newAccessToken}`;
            } else {
                clearUser();
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            clearUser();
        }
    }
    return config;
});

axiosService.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (redirectIfDisabled(error)) return Promise.reject(error);

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const newToken = await refreshAccessToken();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axiosService(originalRequest);
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                clearUser();
            }
        }
        return Promise.reject(error);
    }
);

// Concurrent callers (axios interceptors, privilege sync, etc.) share a single
// in-flight refresh instead of each racing their own /user/refreshToken call
// with the same (soon-to-be-stale) refresh token.
let refreshInFlight = null;

export function refreshAccessToken() {
    if (refreshInFlight) {
        return refreshInFlight;
    }

    refreshInFlight = (async () => {
        try {
            const currRefreshToken = store.getState().auth.refreshToken;

            if (!currRefreshToken) {
                throw new Error('No refresh token available');
            }

            const res = await axios.post(`${VITE_API_URL}/user/refreshToken`, {
                refreshToken: currRefreshToken,
            });

            if (res && res.data) {
                const { token, refreshToken, accessExpiry, user } = res.data;

                store.dispatch(
                    authSlice.actions.setAuthTokens({
                        token,
                        refreshToken,
                        accessExpiry,
                    })
                );

                // Merge so fields the refresh payload omits (profilePhoto, and
                // anything added later) are not wiped off the stored user.
                if (user) {
                    const currentUser = store.getState().auth.user;
                    store.dispatch(authSlice.actions.setUser(
                        currentUser ? { ...currentUser, ...user } : user
                    ));
                }

                return token;
            } else {
                return '';
            }
        } catch (error) {
            if (!redirectIfDisabled(error)) console.error('Error refreshing token:', error);
            throw error;
        }
    })();

    return refreshInFlight.finally(() => {
        refreshInFlight = null;
    });
}

export function fetcher<T = any>(url: string) {
    return axiosService.get<T>(url).then((res) => res.data);
}

export default axiosService;
